<template>
    <x-data-table
        class="no-row-hover double-rows cell-vertical-align-top cell-padding-vertical"
        :headers="headers"
        :items="formattedData"
        hide-actions
    >
        <template v-slot:no-data>
            <p class="error-message text-xs-center">No firm order offers found</p>
        </template>

        <template v-slot:items="props">
            <tr>
                <td>
                    {{ props.item.insurer || props.item.captive_insurer }}
                    <template v-if="props.item.underwriter_reference"><br>Reference: {{ props.item.underwriter_reference }}</template>
                    <template v-if="props.item.risk_code"><br>Risk code: {{ props.item.risk_code }}</template>
                    <template v-if="props.item.insurer_role"><br>Role: {{ props.item.insurer_role }}</template>
                </td>
                <td class="text-xs-right">{{ props.item.brokerage | percent }}</td>
                <td class="text-xs-right">
                    {{ props.item.written_line_amount | currency(submissionCurrency) }}
                    <br>
                    <x-tooltip bottom>
                        <template v-slot:activator="{ on }">
                                <span v-on="on">
                                    {{ props.item.written_line_percent_display | percent(4) }}
                                </span>
                        </template>

                        <span>{{ props.item.written_line_percent | percent(12) }}</span>
                    </x-tooltip>
                </td>
                <td v-if="props.item.price" class="text-xs-right">
                    {{ props.item.price }} BPS
                    <template v-if="props.item.percentage_of_margin"><br>({{ props.item.percentage_of_margin | percent }} of {{ typeOfMargin(props.item.type) }} margin)</template>
                </td>
                <td v-else-if="props.item.flat_fee" class="text-xs-right">{{ props.item.flat_fee | currency(submissionCurrency) }}</td>
                <td class="text-xs-right">{{ firmOrder.waiting_period }} days</td>
                <td class="text-xs-right" v-if="firmOrderHasWaitingPeriodInterest">
                    <x-tooltip top>
                        <template v-slot:activator="{ on }">
                            <div v-on="on">
                                {{
                                    props.item.maximum_insured_exposure | currency(submissionCurrency)
                                }}
                            </div>
                        </template>
                        <p>
                            Written line: {{ props.item.written_line_amount | currency(submissionCurrency) }}
                        </p>
                        <p>
                            Interest rate: {{ props.item.waiting_period_interest_rate | percent }}
                        </p>
                        <p>
                            Waiting period interest: {{ props.item.waiting_period_interest | currency(submissionCurrency) }}
                        </p>
                        <p>
                            Total: {{ props.item.maximum_insured_exposure | currency(submissionCurrency) }}
                        </p>
                    </x-tooltip>
                </td>

                <td class="text-xs-center">
                    <status-chip :status="props.item.status" small />
                </td>
            </tr>
            <tr>
                <td class="note-cell" :colspan="colspan">
                    <span v-if="props.item.security">Security: {{ props.item.security }}</span>
                </td>
            </tr>
        </template>
    </x-data-table>
</template>

<script>
    import StatusChip from '~/components/Shared/UIComponents/StatusChip';

    export default {
        name: 'BrokerFirmOrderTable',
        props: ['firmOrder', 'submissionCurrency'],
        components: {
            StatusChip
        },
        methods: {
            typeOfMargin(type) {
                if (type === 'percentage_of_net_margin') {
                    return 'net';
                } else if (type === 'percentage_of_gross_margin') {
                    return 'gross';
                }
            },
        },
        computed: {
            headers() {
                const headers = [
                    {
                        text: 'insurer / security',
                        value: 'insurer'
                    },
                    {
                        text: 'brokerage',
                        value: 'brokerage',
                        align: 'right'
                    },
                    {
                        text: 'written line',
                        value: 'written_line',
                        align: 'right'
                    },
                    {
                        text: 'premium rate',
                        value: 'price',
                        align: 'right'
                    },
                    {
                        text: 'waiting period',
                        value: 'waiting_period',
                        align: 'right'
                    },
                ];
                if (this.firmOrderHasWaitingPeriodInterest) {
                    headers.push({
                        text: 'maximum insured exposure',
                        value: 'max_insured_exposure',
                        align: 'right',
                    });
                }
                headers.push({
                        text: 'status',
                        value: 'status',
                        align: 'center'
                    });
                return headers;

            },
            formattedData() {
                return this.firmOrder.firm_order_offers.map((firmOrderOffer) => {
                    return {
                        'insurer': (firmOrderOffer.insurer_company !== null) ? firmOrderOffer.insurer_company.name : '',
                        'captive_insurer': (firmOrderOffer.captive_insurer_company !== null) ? firmOrderOffer.captive_insurer_company.name : '',
                        'written_line': firmOrderOffer.written_line_amount,
                        'underwriter_reference': firmOrderOffer.underwriter_reference,
                        'risk_code': firmOrderOffer.risk_code,
                        'brokerage': firmOrderOffer.offer.brokerage,
                        'written_line_amount': firmOrderOffer.written_line_amount,
                        'written_line_percent': firmOrderOffer.written_line_percent,
                        'written_line_percent_display': firmOrderOffer.written_line_percent_display,
                        'price': firmOrderOffer.price,
                        'percentage_of_margin': firmOrderOffer.percentage_of_margin,
                        'flat_fee': firmOrderOffer.flat_fee,
                        'base_estimated_premium': firmOrderOffer.base_estimated_premium,
                        'estimated_premium': firmOrderOffer.estimated_premium,
                        'status': firmOrderOffer.status,
                        'security': (firmOrderOffer.security_id !== null) ? firmOrderOffer.security.name : '',
                        'maximum_insured_exposure': firmOrderOffer.maximum_insured_exposure,
                        'waiting_period_interest': firmOrderOffer.waiting_period_interest,
                        'manual_estimated_premium': firmOrderOffer.manual_estimated_premium,
                        'waiting_period_interest_rate': this.firmOrder.waiting_period_interest_rate,
                        'type': firmOrderOffer.offer.type,
                        'insurer_role': firmOrderOffer.insurer_role || null,
                    }
                });
            },
            firmOrderHasWaitingPeriodInterest() {
                if (this.firmOrder.waiting_period_interest_rate !== null) {
                    return true;
                }
                return false;
            },
            colspan() {
                return (this.firmOrderHasWaitingPeriodInterest) ? 9 : 7;
            }
        }
    }
</script>

<style lang="scss" scoped>
    .x-table.x-datatable tbody tr td.note-cell {
        height: auto;
        padding-top: 0;
        padding-bottom: 0;

        > span {
            display: inline-block;
            padding-bottom: 8px;
        }
    }
</style>
